import html2canvas from 'html2canvas-pro'
import { jsPDF } from 'jspdf'
import { useRef, useTransition } from 'react'

import { useToast } from '#hooks/use-toast'
import { useUploadFile } from '#hooks/use-upload-file'
import { fileToFileDto } from '#lib/file-utils'

const A4_PAGE_WIDTH = 210 // mm
const A4_PAGE_HEIGHT = 297 // mm

export const useRenderPdf = ({ fileName, margin }: { fileName: string; margin: number }) => {
  const pdfRef = useRef<HTMLDivElement>(null)
  const [isPending, startTransition] = useTransition()
  const { handleError } = useToast()
  const { uploadFile } = useUploadFile()

  const downloadPDF = async (): Promise<void> => {
    return new Promise((resolve) => {
      startTransition(async () => {
        try {
          const element = pdfRef.current
          if (!element) {
            return
          }

          const pdf = await generatePDF(element, margin)

          const pdfBlob = pdf.output('blob')
          const link = document.createElement('a')
          link.href = URL.createObjectURL(pdfBlob)
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          resolve()
        } catch (error) {
          handleError(error)
        }
      })
    })
  }

  const toPDF = async (): Promise<ReturnType<typeof uploadFile>> => {
    return new Promise((resolve) => {
      startTransition(async () => {
        try {
          const element = pdfRef.current
          if (!element) {
            return
          }

          const pdf = await generatePDF(element, margin)

          const pdfBlob = pdf.output('blob')
          const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' })
          const fileDto = fileToFileDto(pdfFile)
          const uploadedFile = await uploadFile(fileDto)
          resolve(uploadedFile)
        } catch (error) {
          handleError(error)
        }
      })
    })
  }

  return { pdfRef, toPDF, downloadPDF, isPending }
}

async function generatePDF(element: HTMLElement, margin: number): Promise<jsPDF> {
  const canvas = await html2canvas(element, { useCORS: true })
  const pxToMM = A4_PAGE_WIDTH / element.clientWidth
  const pdf = new jsPDF('p', 'mm', [A4_PAGE_WIDTH, A4_PAGE_HEIGHT])

  let currentPageHeight = 0
  let totalCanvasAdded = 0

  function processElement(currentElement: Element) {
    const children = Array.from(currentElement.children)
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      const nextChild = children[i + 1] ?? child
      const childMargin = getMargin(child, nextChild)
      const childHeight = child.clientHeight * pxToMM
      const totalChildHeightWithMargin = childHeight + childMargin * pxToMM

      if (childHeight > A4_PAGE_HEIGHT - margin * 2) {
        processElement(child)
      } else {
        if (currentPageHeight + totalChildHeightWithMargin >= A4_PAGE_HEIGHT - margin * 2) {
          const canvasHeight = currentPageHeight * (canvas.width / A4_PAGE_WIDTH)
          addImage(pdf, canvas, totalCanvasAdded, canvasHeight, margin)
          pdf.addPage()
          totalCanvasAdded += canvasHeight
          currentPageHeight = 0
        }
        currentPageHeight += totalChildHeightWithMargin
      }
    }
  }

  processElement(element)
  const canvasHeight = Math.max(0, canvas.height - totalCanvasAdded)
  if (canvasHeight > 0) {
    addImage(pdf, canvas, totalCanvasAdded, canvasHeight, margin)
  }

  return pdf
}

function addImage(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  canvasOffYset: number,
  canvasHeight: number,
  margin: number
) {
  const pageWidth = A4_PAGE_WIDTH - margin * 2
  const canvasPage = document.createElement('canvas')

  canvasPage.setAttribute('width', String(canvas.width))
  canvasPage.setAttribute('height', String(canvasHeight))

  const ctx = canvasPage.getContext('2d')
  ctx?.drawImage(
    canvas,
    0,
    canvasOffYset,
    canvas.width,
    canvasHeight,
    0,
    0,
    canvas.width,
    canvasHeight
  )

  const imgData = canvasPage.toDataURL('image/png')

  pdf.addImage(
    imgData,
    'png',
    margin,
    margin,
    pageWidth,
    canvasHeight * (pageWidth / canvas.width),
    undefined,
    'FAST'
  )
}

function getMargin(topElement: Element, bottomElement: Element) {
  const stylesTopElement = window.getComputedStyle(topElement)
  const stylesBottomElement = window.getComputedStyle(bottomElement)

  const styleBottomElementFirstChild = bottomElement.children[0]
    ? window.getComputedStyle(bottomElement.children[0])
    : { marginBottom: '0px' }

  const marginBottom = Number.parseFloat(stylesTopElement.marginBottom.replace('px', ''))
  const marginTop = Number.parseFloat(stylesBottomElement.marginTop.replace('px', ''))
  const marginBottomFirstChild = Number.parseFloat(
    styleBottomElementFirstChild.marginBottom.replace('px', '')
  )

  const border = Number.parseFloat(stylesTopElement.borderBottomWidth.replace('px', '')) * 2

  return Math.max(marginTop, marginBottom, marginBottomFirstChild) + border
}
